import React from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useBackendApi from '../../hooks/useBackendApi';
import useClearableState from '../../hooks/useClearableState';
import useGclidTracking from '../../hooks/useGclidTracking';
import useLoaderStore from '../../hooks/useLoaderStore';
import useProviderProfilesStore from '../../hooks/useProviderProfilesStore';
import useQuoteDataStore from '../../hooks/useQuoteDataStore';
import useToggle from '../../hooks/useToggle';
import { IOffer } from '../../types/quote/offer';
import {
  addToSerchParams,
  clearUserDataFromStorage,
  postEmail,
  replaceUrlParamsWithValue,
} from '../../utils';
import useFomo from './useFomo';

export default function useProviderProfilePageContainerData(
  url,
  mobileOrStudio,
  checkAvailabilityUrl,
  key?
) {
  const { pushGclidToDataLayer } = useGclidTracking({ trackOnInit: false });
  const history = useHistory();
  const params = useParams<any>();

  const { get, post, del } = useBackendApi();
  const newDateTimeModal = useToggle();
  const contactModal = useToggle();
  const cancellationPolicyModal = useToggle();
  const dateTimeChangeSuccessModal = useToggle();
  const checkAvailabilityModal = useToggle();
  const checkAvailabilitySuccessModal = useToggle();
  const contactProviderModal = useToggle();
  const contactProviderSuccessModal = useToggle();
  const bookingCancellationModal = useToggle();
  const changePackageModal = useToggle();
  const changeModal = useToggle();
  const loginModal = useToggle();

  const { loggedIn } = useAuth();
  const [pendingAction, setPendingAction] = React.useState<string>('');

  const [successMsg, setSuccessMsg, clearSuccessMsg] = useClearableState('');
  const [errMsg, setErrMsg, clearErrMsg] = useClearableState('');

  const rescheduleModal = useToggle();

  const { hideLoader, showLoader } = useLoaderStore();

  const { providerProfiles, setProfileItem, updateStatus } =
    useProviderProfilesStore();
  const { appendRequestToQuoteData } = useQuoteDataStore();
  const { profileId, offerId, quoteId } = params;
  const idField =
    ((profileId && `${profileId}_${quoteId}`) || offerId) +
    `_${mobileOrStudio}`;

  const [loading, setLoading] = React.useState<boolean>(false);
  const profileItem = React.useMemo(() => {
    const item = providerProfiles[idField];
    return item;
  }, [providerProfiles, idField]);

  const { watchersCount } = useFomo({
    searchId: profileItem?.search?._id,
    isFeatured: profileItem?.provider?.isFeatured,
    profileId: profileItem?.provider?.profileId,
  });

  React.useEffect(() => {
    const fetch = async () => {
      const params: any = {};
      if (key) {
        params.key = key;
      }
      if (mobileOrStudio) {
        params.mobileOrStudio = mobileOrStudio;
      }
      if (!profileItem) {
        setLoading(true);
      }
      const res = await get(url, params, { loader: false });
      setLoading(false);

      const payload: IOffer = res.data;

      const otherOffers =
        payload.appointments?.[mobileOrStudio]?.filter(
          ({ provider }) => provider.profileId !== payload.provider.profileId
        ) || [];
      payload.otherOffers = otherOffers;

      const customerId = payload.customer?._id;
      if (payload.search?.gclid) {
        pushGclidToDataLayer(payload.search.gclid);
      }
      setTimeout(() => {
        const heap = window['heap'];
        if (payload.search.heapId && heap) {
          heap.identify(payload.search.heapId);
        }
      }, 0);
      setProfileItem({ [idField]: payload });

      const dataLayer = window['dataLayer'] || [];
      if (customerId) {
        const data = { user_id: customerId, event: 'userIdSet' };
        dataLayer.push(data);
      }

      if (payload.search?._id) {
        let ecommerceItem: any = {
          item_name: payload.search?._id, // Name or ID is required.
          price: payload.offer?.pricing?.amount.toString(),
          currency: payload?.offer?.pricing?.currency,
          item_brand: profileId || payload.provider?.profileId,
          item_category: payload.search?.categorySlug,
          item_category2: payload?.offer?.location?.mobileOrStudio,
          quantity: '1',
        };
        const itemId = payload.offer?._id;
        if (itemId) {
          ecommerceItem['item_id'] = itemId;
        }

        const data = {
          event: 'view_item',
          ecommerce: {
            items: [ecommerceItem],
          },
        };
        dataLayer.push(data);
      }
    };

    if (url) {
      fetch();
    }
  }, [url]);

  function handleCtaClick() {
    if (!profileItem?.offer) {
      return history.push('/');
    }
    switch (profileItem.offer?.status) {
      case 'requested':
      case 'changePackageRequested':
        return;
      case 'changePackageOffered':
        return history.push(`/offers/${profileItem.offer?.replacedWith}`);
      case 'offered':
      case 'timeChangeRequested':
        return history.push({
          pathname: `/book/offer/${profileItem.offer?._id}/${profileItem.offer?.key}`,
          state: { idField },
        });
      case 'offerExpired':
        return requestAgain();
      case 'booked':
      case 'cancelled':
      case 'cancelledByCustomer':
      case 'cancelledByProvider':
      case 'cancelledByFlayr':
      case 'rescheduleRequested':
      case 'closed':
        return contactModal.toggle();
      case 'rescheduleOffered':
        if (!loggedIn) {
          setPendingAction('acceptReschedule');
          return loginModal.toggle();
        }
        return handleAcceptReschedule();
      default:
        const cahcedSearch = localStorage.getItem('checkAvailabilitySearchId');
        if (cahcedSearch === profileItem.search?._id) {
          return handleCheckAvailabilityFormSubmit();
        }
        return checkAvailabilityModal.toggle();
    }
  }

  async function requestAgain() {
    if (profileItem.offer) {
      const res = await post(
        replaceUrlParamsWithValue(`/v1/offers/:offerId/requestAgain`, {
          offerId: profileItem.offer?._id,
        })
      );

      setProfileItem({
        [idField]: res.data,
      });
    }
  }

  function handleTimeChangeSubmit(e) {
    updateStatus({
      field: idField,
      status: e?.status,
    });
  }

  function handleBack() {
    const searchId = profileItem?.search?._id;
    if (!searchId) {
      return history.push('/');
    }
    let searchParam = '';
    if (mobileOrStudio === 'studio') {
      searchParam = `?mobileOrSutdio=${mobileOrStudio}`;
    }
    let url = `/quotes/${searchId}${searchParam}`;
    history.replace(url);
  }

  function handleCheckAvailabilityFinish(data) {
    const search = addToSerchParams(history.location.search, {
      stage: 'submitted',
      form: 'checkavailability',
    });
    history.replace({
      pathname: history.location.pathname,
      search,
    });
    setProfileItem(data);
    appendRequestToQuoteData(data);

    updateStatus({
      field: idField,
      status: data.offer?.status,
    });
    const customerId = data.customer?._id;
    if (customerId) {
      const dataLayer = window['dataLayer'];
      const data = { user_id: customerId };
      dataLayer.push(data);
    }

    localStorage.setItem('checkAvailabilitySearchId', profileItem.search?._id);
  }

  async function handleCheckAvailabilityFormSubmit(value?) {
    if (!value) {
      const formInput = localStorage.getItem('appointmentFormValues');
      if (formInput && formInput !== 'undefined') {
        value = JSON.parse(formInput);
      }
    }
    const finishTime =
      profileItem.search?.appointmentSpot.finishTime || value?.finishTime;
    const res = await post(checkAvailabilityUrl, {
      ...value,
      finishTime,
      mobileOrStudio:
        mobileOrStudio || profileItem.search?.location.mobileOrStudio,
    });
    const data = res.data;
    handleCheckAvailabilityFinish(data);
    checkAvailabilityModal.close();
    checkAvailabilitySuccessModal.open();
  }

  async function handleContactProviderFormSubmit(info) {
    showLoader();
    const subject = 'Contact provider';
    const body = `
      <p>
        Customer name: ${info.name} <br />
        Customer email: ${info.email} <br />
        Customer mobile: ${info.mobile}
      <p/>

      <p>
        Looking to contact provider ${profileItem.provider.name} <br />
        profile Id: ${profileItem.provider.profileId}
      <p/>

      <p>
        message: <br /> ${info.message}
      </p>
    `;
    await postEmail(
      { emails: ['hey@flayr.life'], subject, body },
      { email: info.email, name: info.name }
    );
    localStorage.setItem('checkAvailabilitySearchId', profileItem.search?._id);
    hideLoader();
    contactProviderModal.close();
    contactProviderSuccessModal.open();
  }

  function handleOfferExpired() {
    updateStatus({
      field: idField,
      status: 'offerExpired',
    });
  }

  function handleCancelBookingComplete(data: any) {
    updateStatus({
      field: idField,
      status: data?.offer?.status,
    });
  }

  function handleRescheduleComplete(data: any) {
    setProfileItem({
      [idField]: data,
    });
  }

  async function handleAcceptReschedule() {
    const url = replaceUrlParamsWithValue(
      `/v1/offers/:offerId/reschedule/acceptOffer`,
      { offerId }
    );
    const res = await post(
      url,
      {},
      {
        onError: (err) => {
          setErrMsg(err.message);
          clearUserDataFromStorage();
          setLoading(false);
        },
      }
    );
    handleRescheduleComplete?.(res.data);
    const msg = 'Offer Accepted';
    setSuccessMsg(msg);
  }

  async function handleCancelChangeRequest() {
    const url = replaceUrlParamsWithValue('/v1/offers/:offerId/changePackage', {
      offerId,
    });
    const res = await del(
      url,
      {},
      {
        onError: (err) => {
          setErrMsg(err.message);
        },
      }
    );
    setProfileItem({
      [idField]: res.data,
    });
    const msg = 'Request cancelled';
    setSuccessMsg(msg);
  }

  function handleChangePackage() {
    if (!loggedIn) {
      setPendingAction('changePackage');
      return loginModal.toggle();
    }
    changePackageModal.toggle();
  }

  function handleReschedule() {
    if (!loggedIn) {
      setPendingAction('reschedule');
      return loginModal.toggle();
    }
    rescheduleModal.toggle();
  }

  function handleLoginComplete() {
    switch (pendingAction) {
      case 'acceptReschedule':
        return handleAcceptReschedule();
      case 'changePackage':
        return changePackageModal.toggle();
      case 'reschedule':
        return rescheduleModal.toggle();
    }
  }

  async function handleChangePackageRequestSubmit() {}

  function handleChangeOptionSelect(_e, _id, option) {
    changeModal.toggle();
    setTimeout(() => {
      switch (option) {
        case 'reschedule':
          return handleReschedule();
        case 'change package':
          return handleChangePackage();
        case 'cancel':
          bookingCancellationModal.toggle();
          break;
      }
    }, 0);
  }

  const dataFromPreviousPage: any = history.location.state;

  return {
    ...profileItem,
    provider: profileItem?.provider || dataFromPreviousPage?.provider || {},
    offerId: profileItem?.offer?._id || params.offerId,
    profileId: params.profileId || profileItem?.provider?.profileId,
    loading,
    key,
    newDateTimeModal,
    contactModal,
    cancellationPolicyModal,
    dateTimeChangeSuccessModal,
    checkAvailabilityModal,
    checkAvailabilitySuccessModal,
    contactProviderModal,
    watchersCount,
    contactProviderSuccessModal,
    handleOfferExpired,
    handleCheckAvailabilityFormSubmit,
    handleCheckAvailabilityFinish,
    handleContactProviderFormSubmit,
    handleTimeChangeSubmit,
    handleCtaClick,
    handleBack,
    bookingCancellationModal,
    handleCancelBookingComplete,
    rescheduleModal,
    handleRescheduleComplete,
    successMsg,
    clearSuccessMsg,
    errMsg,
    clearErrMsg,
    handleAcceptReschedule,
    handleCancelChangeRequest,
    loginModal,
    changePackageModal,
    changeModal,
    handleChangeOptionSelect,
    handleChangePackageRequestSubmit,
    handleChangePackage,
    handleLoginComplete,
  };
}
